// src/pages/Session.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';

// Import all child components
import Reservations from '../components/sessions/Reservations';
import Attendees from '../components/sessions/Attendees';
import ProfitLoss from '../components/sessions/ProfitLoss';
import PaymentManagement from '../components/sessions/PaymentManagement';
import PromotionModal from '../components/sessions/PromotionModal';

// --- Styled Components for the New Hero Section (Unchanged) ---
const HeroImage = styled(Box)(({ image }) => ({
    width: '100%',
    height: '45vh',
    maxHeight: '480px',
    backgroundImage: `linear-gradient(to top, rgba(28,28,28,1) 15%, rgba(28,28,28,0.4) 70%, rgba(28,28,28,0) 100%), url(${image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    transition: 'background-image 1s ease-in-out',
}));

const HeaderContent = styled(Box)({
    textAlign: 'center',
    maxWidth: '800px',
    margin: '-100px auto 4rem auto',
    position: 'relative',
    padding: '0 1rem',
});

const Footer = () => (
    <Box
        component="footer"
        sx={{ mt: 8, py: 4, borderTop: '1px solid #404040', textAlign: 'center' }}
    >
        <Typography variant="body2" sx={{ color: '#aaa' }}>
            © {new Date().getFullYear()} Mars Reel Media Corp. All rights reserved.
        </Typography>
    </Box>
);

const fetchUserDetails = async (firebaseUid) => {
    try {
        const endpoints = [`/api/athlete/${firebaseUid}`, `/api/coach/${firebaseUid}`, `/api/site-manager/${firebaseUid}`];
        const responses = await Promise.allSettled(endpoints.map(url => axios.get(`${process.env.REACT_APP_BACKEND_URL}${url}`)));
        const [athleteRes, coachRes, siteManagerRes] = responses;

        if (athleteRes.status === 'fulfilled' && athleteRes.value.data) {
            return { type: 'athlete', name: `${athleteRes.value.data.firstName} ${athleteRes.value.data.lastName}`.trim(), firebaseUid };
        }
        if (coachRes.status === 'fulfilled' && coachRes.value.data) {
            return { type: 'coach', name: coachRes.value.data.name || 'Unknown Coach', firebaseUid };
        }
        if (siteManagerRes.status === 'fulfilled' && siteManagerRes.value.data) {
            return { type: 'siteManager', name: `${siteManagerRes.value.data.firstName} ${siteManagerRes.value.data.lastName}`.trim(), firebaseUid };
        }
        return { type: 'unknown', name: 'Unknown User', firebaseUid };
    } catch (error) {
        console.error(`Error fetching details for ${firebaseUid}:`, error);
        return { type: 'unknown', name: 'Error Fetching Name', firebaseUid };
    }
};

// --- Main Page Component ---
const Session = () => {
    const { id } = useParams();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const [promoModalOpen, setPromoModalOpen] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [loadingProspects, setLoadingProspects] = useState(false);

    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);

    useEffect(() => {
        const fetchSessionDetails = async () => {
            setLoading(true);
            try {
                const sessionResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/session/${id}`);
                const sessionData = sessionResponse.data;

                const [detailedReservations, detailedAttendees] = await Promise.all([
                    Promise.all((sessionData.reservedBy || []).map(fetchUserDetails)),
                    Promise.all((sessionData.attendees || []).map(fetchUserDetails))
                ]);

                setSession({ ...sessionData, reservedBy: detailedReservations, attendees: detailedAttendees, profitLossData: sessionData.profitLossData || {} });
            } catch (error) {
                console.error('Error fetching session details:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSessionDetails();
    }, [id]);

    useEffect(() => {
        if (!session) return;

        const images = [session.venueImage1, session.venueImage2, session.venueImage3].filter(Boolean);
        if (images.length > 1) {
            const timer = setInterval(() => {
                setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [session]);

    const handleOpenPromoModal = () => {
        setPromoModalOpen(true);
        setLoadingProspects(true);
        setSendError(null);

        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/session/${id}/prospects`)
            .then((res) => {
                const leads = res.data.prospects || [];
                const enrichedProspects = leads.map((lead) => ({
                    id: lead.id,
                    name: lead.name,
                    email: lead.email,
                    phone: lead.phone,
                    city: lead.location || 'N/A',
                    matchType: lead.matchType || 'N/A',
                }));
                setProspects(enrichedProspects);
            })
            .catch((err) => {
                console.error('Error fetching prospects:', err);
                alert('Failed to load prospects. Please check the console for details.');
            })
            .finally(() => {
                setLoadingProspects(false);
            });
    };

    const handleSendPromotions = async (recipientsToSend) => {
        if (recipientsToSend.length === 0) return alert("Please select at least one recipient.");
        setSendError(null);

        if (!window.confirm(`You are about to send promotional emails to ${recipientsToSend.length} people. Are you sure?`)) return;

        setSending(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/session/${id}/send-promotions`,
                { recipients: recipientsToSend }
            );

            alert(response.data.message || `Successfully queued emails for ${recipientsToSend.length} recipients!`);
            setPromoModalOpen(false);

        } catch (error) {
            console.error('Error sending promotions:', error.response?.data || error);
            const errorMessage = error.response?.data?.message || 'Failed to send promotions due to a server error.';
            setSendError(errorMessage);
            alert(`Sending Failed: ${errorMessage}`);
        } finally {
            setSending(false);
        }
    };

    // ✨ NEW: Function to handle the blast email API call
    const handleSendBlast = async () => {
        setSendError(null);

        // A stronger confirmation for a more critical action
        if (!window.confirm("⚠️ You are about to send a promotional email to ALL users and leads. This action cannot be undone. Are you sure you want to proceed?")) return;

        setSending(true);

        try {
            // --- SEND BLAST ENDPOINT: POST /api/session/:id/blast ---
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/session/${id}/blast`
                // No request body is needed as the backend finds all users
            );

            alert(response.data.message || 'Successfully queued email blast!');
            setPromoModalOpen(false); // Close modal on success

        } catch (error) {
            console.error('Error sending blast:', error.response?.data || error);
            const errorMessage = error.response?.data?.message || 'Failed to send email blast due to a server error.';
            setSendError(errorMessage);
            alert(`Sending Failed: ${errorMessage}`);
        } finally {
            setSending(false);
        }
    };


    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1c1c1c' }}><CircularProgress color="primary" /></Box>;
    }
    if (!session) {
        return <Box sx={{ p: 4, backgroundColor: '#1c1c1c' }}><Alert severity="error">Session not found.</Alert></Box>;
    }

    const venueImages = [session.venueImage1, session.venueImage2, session.venueImage3].filter(Boolean);
    const currentImage = venueImages.length > 0 ? venueImages[currentImageIndex] : 'https://via.placeholder.com/1200x600?text=Event+Image';

    return (
        <Box sx={{ backgroundColor: '#1c1c1c', color: '#fff', minHeight: '100vh' }}>
            <HeroImage image={currentImage} />
            <HeaderContent>
                <Typography variant="h2" component="h1" sx={{ fontWeight: 700, letterSpacing: '-1.5px', mb: 1, color: '#fff' }}>
                    {session.name}
                </Typography>
                <Typography variant="h6" sx={{ color: '#aaa', mb: 2, fontWeight: 400 }}>
                    {`${new Date(session.eventDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })} · ${new Date(session.eventStartTime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    })} to ${new Date(session.eventEndTime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    })}`}
                </Typography>
                <Typography variant="body1" sx={{ color: '#ccc', mb: 3 }}>
                    {session.description}
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleOpenPromoModal}
                    sx={{
                        backgroundColor: '#12c099', color: 'white', fontWeight: 'bold',
                        textTransform: 'none', borderRadius: '8px', px: 4, py: 1.5,
                        fontSize: '1.1rem', '&:hover': { backgroundColor: '#0f9f85' }
                    }}
                >
                    Promote Session
                </Button>
            </HeaderContent>

            {sendError && (
                <Alert severity="error" onClose={() => setSendError(null)} sx={{ position: 'fixed', top: 20, right: 20, zIndex: 1300 }}>
                    {sendError}
                </Alert>
            )}

            <Box sx={{ padding: '0 2rem' }}>
                <PaymentManagement
                    coaches={(session.coachNames ?? []).map((name, i) => ({ name, rate: session.coachRate ?? 0, owed: (session.coachRate ?? 0) * 2, firebaseUid: session.coachUids?.[i] ?? '' }))}
                    siteManagers={(session.siteManagerNames ?? []).map((name, i) => ({ name, rate: session.siteManagerRate ?? 0, owed: (session.siteManagerRate ?? 0) * 2, firebaseUid: session.siteManagerUids?.[i] ?? '' }))}
                    attendees={(session.attendees ?? []).map(attendee => attendee.firebaseUid ?? '')}
                    sessionId={id}
                />
                <Reservations reservations={session.reservedBy} />
                <Attendees attendees={session.attendees} />
                <ProfitLoss profitLossData={session.profitLossData} />
            </Box>

            <Footer />

            <PromotionModal
                open={promoModalOpen}
                onClose={() => setPromoModalOpen(false)}
                session={session}
                prospects={prospects}
                loading={loadingProspects || sending}
                onSend={handleSendPromotions}
                sending={sending}
                onSendBlast={handleSendBlast} // ✨ ADD THIS PROP to connect the button to the function
            />
        </Box>
    );
};

export default Session;
